/**
 * The view-model of the piece as a whole — **phase 2**.
 *
 * Pure TS: no React, no Studio, no DOM. Sibling of `rhythm.ts`, which reads a
 * single pass, and of `practice.ts`, which drives the runs without rhythm.
 *
 * ## Why the piece is not just a very long unit
 *
 * Phase 1 works one unit at a time, and `work-units.ts` exists because a run
 * of eighty notes buys **one** repetition of each transition where motor
 * consolidation wants five to twenty. Phase 2 was built by ignoring that at
 * the level above: it played the **whole piece** in one go. Measured over the
 * PDMX testset (200 pieces, 10 530 units):
 *
 * - a unit is **12 attacks** in the median; a piece is **531** (p90 1 247,
 *   max 7 448) — a factor of **39.3**;
 * - a traversal at the tempo the piece actually holds takes **6 min 12** in
 *   the median (p90 13 min 59), and **only 19.0% of pieces (38 / 200)** can
 *   be crossed in three minutes or less.
 *
 * So the traversal cannot be the training loop. What it *is* — and nothing
 * else in the journey can be it — is the **instrument**: the standard error
 * on a share of honoured attacks is ±8.7 points on a unit of 12 and **±1.3
 * points on a piece of 531**. It is the only object of the journey on which a
 * statement about the whole is not noise. Once per session, as a reading and
 * as an endurance test; never as a loop.
 *
 * ## One tempo, and it is the spec's
 *
 * `docs/spec/parcours-apprentissage-morceau.md` asks for a single tempo for
 * the whole piece, "sinon on retombe sur le piège des fragments déconnectés
 * que la phase 1 cherchait à éviter". That is right, and it is what being a
 * piece means. What was wrong was the **three fixed tiers** (75/90/100):
 * **70.3% of units (7 406 / 10 530) open below 0.75**, so the floor of phase
 * 2 sat *above* where phase 1 leaves almost everyone, and the real climb is
 * **9 rungs of `RATE_LADDER` in the median** — from 49 BPM to 106 — not two.
 *
 * The piece opens at the tempo it holds **everywhere**, which is its slowest
 * unit. And there is no hostage to free: the minimum is reached by a single
 * unit in only **15.5% of pieces (31 / 200)**; the brake is **12 units
 * carrying 43% of the attacks**. There is a substantial part of the piece to
 * consolidate, session after session — which is why the work list, and not
 * the traversal, is the act this phase repeats.
 */

import type { AnalysisSection } from '@/lib/music/analyzer-types';
import type { Phrase } from '@/lib/learning-journey/types';
import type { PracticeGroup } from './practice';
import {
  PLAYED_THROUGH_RATIO,
  rhythmGroups,
  startingRate,
  type Interruption,
  type RunReading,
} from './rhythm';

// ── The rail's cells ──────────────────────────────────────────────

/** One stretch of the piece, drawn as one cell of the rail. */
export type PieceSection = {
  readonly id: string;
  /** The analyzer's name for it, or `null` when it only has bars. */
  readonly label: string | null;
  readonly startTick: number;
  readonly endTick: number;
  readonly startMeasure: number;
  readonly endMeasure: number;
  /** Indices into the journey's units, in order. */
  readonly unitIndices: readonly number[];
};

/**
 * Bars per cell when the piece has no usable sections.
 *
 * The rail never disappears: it is the only thing on the whole screen that
 * says where the player is inside their piece, and the roll shows about 3% of
 * it at a time. A piece without sections gets regular blocks and cells named
 * by their bars rather than no rail at all.
 */
const FALLBACK_BARS_PER_CELL = 8;

/**
 * Cells the rail may draw before a cell stops being a target.
 *
 * At 504 px of rail, a floor of 28 px per cell and a 3 px gap, sixteen is
 * what fits. Beyond it the piece is offered whole. Measured, **76.0% of
 * pieces (152 / 200) have between 3 and 12 sections**, so this degrades on
 * the tail rather than on the common case.
 */
export const MAX_RAIL_CELLS = 16;

/**
 * The piece cut into the cells the rail draws.
 *
 * Sections come from the analyzer, which forces its section boundaries to
 * also be phrase boundaries — so a unit sits inside exactly one section, and
 * `Phrase.sectionId` is enough to group them. A section the plan never
 * attached a unit to is dropped: the rail draws what can be played.
 */
export function pieceSections(
  phrases: readonly Phrase[],
  sections: readonly AnalysisSection[],
  ticksPerBar: number,
): readonly PieceSection[] {
  if (phrases.length === 0) {
    return [];
  }

  const byId = new Map<string, number[]>();

  for (let i = 0; i < phrases.length; i += 1) {
    const list = byId.get(phrases[i].sectionId);

    if (list === undefined) {
      byId.set(phrases[i].sectionId, [i]);

      continue;
    }

    list.push(i);
  }

  const named: PieceSection[] = [];

  for (const section of sections) {
    const unitIndices = byId.get(section.id) ?? [];

    if (unitIndices.length === 0) {
      continue;
    }

    named.push({
      id: section.id,
      label: section.label,
      startTick: phrases[unitIndices[0]].startTick,
      endTick: phrases[unitIndices[unitIndices.length - 1]].endTick,
      startMeasure: phrases[unitIndices[0]].startMeasure,
      endMeasure: phrases[unitIndices[unitIndices.length - 1]].endMeasure,
      unitIndices,
    });
  }

  named.sort((a, b) => a.startTick - b.startTick);

  // Every unit accounted for, and few enough cells to be targets: the rail is
  // the analyzer's own reading of the piece.
  const covered = named.reduce((n, s) => n + s.unitIndices.length, 0);

  if (named.length >= 2 && named.length <= MAX_RAIL_CELLS && covered === phrases.length) {
    return named;
  }

  return regularCells(phrases, ticksPerBar);
}

/** Blocks of `FALLBACK_BARS_PER_CELL` bars, for a piece with no sections. */
function regularCells(
  phrases: readonly Phrase[],
  ticksPerBar: number,
): readonly PieceSection[] {
  const span = ticksPerBar > 0 ? ticksPerBar * FALLBACK_BARS_PER_CELL : 0;
  const cells: PieceSection[] = [];
  let current: number[] = [];
  let anchor = phrases[0].startTick;

  const flush = () => {
    if (current.length === 0) {
      return;
    }

    const first = phrases[current[0]];
    const last = phrases[current[current.length - 1]];

    cells.push({
      id: `bars-${first.startMeasure}`,
      label: null,
      startTick: first.startTick,
      endTick: last.endTick,
      startMeasure: first.startMeasure,
      endMeasure: last.endMeasure,
      unitIndices: current,
    });
    current = [];
  };

  for (let i = 0; i < phrases.length; i += 1) {
    if (
      span > 0 &&
      current.length > 0 &&
      phrases[i].startTick - anchor >= span &&
      cells.length < MAX_RAIL_CELLS - 1
    ) {
      flush();
      anchor = phrases[i].startTick;
    }

    current.push(i);
  }

  flush();

  return cells;
}

/** How a cell is named. Bars when the analyzer had no word for it. */
export function sectionName(section: PieceSection): string {
  if (section.label !== null && section.label !== '') {
    return section.label;
  }

  return `Mesures ${section.startMeasure}–${section.endMeasure}`;
}

// ── The piece's tempo ─────────────────────────────────────────────

/**
 * The tempo the piece holds **everywhere**: the slowest of its units.
 *
 * A unit the player has already been through contributes the fastest rate it
 * held (`unitRate`); one they have not contributes the rate phase 1 would
 * open it at. Taking the minimum is the same sentence S5 already says one
 * level down — "au tempo de la plus lente des deux" — applied to a piece.
 *
 * Measured, the spread inside a single piece is **6 rungs in the median**
 * (≥ 2 rungs in 84.5% of pieces, 37 BPM apart), which is exactly why the
 * piece needs a tempo of its own rather than inheriting one.
 */
export function pieceRate(
  phrases: readonly Phrase[],
  rateOf: (index: number) => number | null,
  bpm: number,
  ppq: number,
): number | null {
  let slowest: number | null = null;

  for (let i = 0; i < phrases.length; i += 1) {
    const rate =
      rateOf(i) ?? startingRate(rhythmGroups(phrases[i]), bpm, ppq);

    if (slowest === null || rate < slowest) {
      slowest = rate;
    }
  }

  return slowest;
}

/** Real seconds a span takes at `rate`. */
export function spanSeconds(
  startTick: number,
  endTick: number,
  ppq: number,
  bpm: number,
  rate: number,
): number {
  if (ppq <= 0 || bpm <= 0 || rate <= 0) {
    return 0;
  }

  return ((endTick - startTick) / ppq) * (60 / (bpm * rate));
}

/**
 * How long an act may run before it stops being an act and becomes a sitting.
 *
 * Beyond this the screen offers a section rather than the whole piece: at the
 * tempo a piece opens at, **only 19.0% (38 / 200) cross in three minutes**,
 * against 55.5% at the written tempo. The object has to grow with the tempo,
 * or the first thing phase 2 asks for is a ten-minute commitment. A section
 * costs **34 s in the median** and stays under 90 s in 94.9% of cases.
 */
export const LONG_ACT_SECONDS = 180;

/**
 * The same limit, for a piece that is being kept rather than climbed.
 *
 * At the written tempo a traversal falls to **2 min 45 in the median** (p10
 * 1:06, p90 5:07) against 6 min 07 at the tempo phase 2 opens on — so what
 * made the traversal impossible as a loop disappears at the top of the
 * ladder, and it becomes the session itself rather than a commitment inside
 * one. Keeping 180 s here would offer **44.5% of pieces (89/200)** part by
 * part when the whole thing was the point; at 300 s only the **11.0%
 * (22/200)** that really run past five minutes are broken up.
 */
export const KEPT_ACT_SECONDS = 300;

/** A duration in the words the button uses. Never a decimal. */
export function actDuration(seconds: number): string {
  if (seconds < 90) {
    return `~${Math.max(5, Math.round(seconds / 5) * 5)} s`;
  }

  return `~${Math.round(seconds / 60)} min`;
}

// ── What a traversal saw ──────────────────────────────────────────

/** What the last traversal found in one cell of the rail. */
export type SectionState = {
  /** Share of the cell's attacks that were answered, or `null` if unread. */
  readonly honoured: number | null;
  /** True when the player came off the rails inside this cell. */
  readonly stopped: boolean;
};

/**
 * Re-exported from `rhythm.ts`, where it now lives: it is a threshold on a
 * `RunReading`, and phase 1's steps needed it too. See it there.
 */
export { PLAYED_THROUGH_RATIO } from './rhythm';

/**
 * Read a traversal cell by cell.
 *
 * `groups` and `reading` cover the same span, in the same order — the
 * traversal's onsets — so a cell's state is a slice by tick.
 */
export function readSections(
  cells: readonly PieceSection[],
  groups: readonly PracticeGroup[],
  reading: RunReading | null,
): readonly SectionState[] {
  if (reading === null) {
    return cells.map(() => ({ honoured: null, stopped: false }));
  }

  const stoppedTicks = reading.interruptions.map((i) => i.startTick);

  return cells.map((cell) => {
    let total = 0;
    let answered = 0;

    for (let g = 0; g < groups.length; g += 1) {
      const tick = groups[g].startTick;

      if (tick < cell.startTick || tick >= cell.endTick) {
        continue;
      }

      total += 1;

      if (reading.honoured[g] === true) {
        answered += 1;
      }
    }

    return {
      honoured: total === 0 ? null : answered / total,
      stopped: stoppedTicks.some(
        (tick) => tick >= cell.startTick && tick < cell.endTick,
      ),
    };
  });
}

/**
 * Where on the rail the piece was least answered.
 *
 * The trace parks here when the traversal held all the way through and there
 * is no stop to park on. Strictly below whole, so a rail with nothing to say
 * returns `null` and the caller falls back to the beginning: a clean pass is
 * not a diagnosis, and dropping the player into the middle of one as if it
 * were would be a lie about their own playing.
 *
 * Unread cells (`honoured === null`) never win. They are cells nobody played,
 * not cells played badly, and a trace has by definition nothing to show
 * there.
 */
export function weakestCellTick(
  cells: readonly PieceSection[],
  states: readonly SectionState[],
): number | null {
  let lowest = 1;
  let tick: number | null = null;

  for (let i = 0; i < cells.length; i++) {
    const honoured = states[i]?.honoured;

    if (honoured === null || honoured === undefined || honoured >= lowest) {
      continue;
    }

    lowest = honoured;
    tick = cells[i].startTick;
  }

  return tick;
}

/**
 * The rail's state as the last stored traversal left it.
 *
 * A cell the stored map does not name stays unread — a piece re-analysed
 * between two sessions can come back with different sections, and inventing
 * a state for a cell nobody played would be worse than an empty one.
 */
export function restoreSections(
  cells: readonly PieceSection[],
  stored: readonly {
    readonly id: string;
    readonly honoured: number;
    readonly stopped: boolean;
  }[],
): readonly SectionState[] {
  const byId = new Map(stored.map((s) => [s.id, s]));

  return cells.map((cell) => {
    const entry = byId.get(cell.id);

    if (entry === undefined) {
      return { honoured: null, stopped: false };
    }

    return { honoured: entry.honoured, stopped: entry.stopped };
  });
}

/**
 * How long ago the piece was last crossed, or `null` when it never was.
 *
 * Phase 2 climbs nine rungs over ten to twenty sessions, so a rail restored
 * from storage can be weeks old — and shown without a date it reads as
 * current. The line says how old it is and nothing else: no streak, no count
 * of days missed, no automatic penalty. The tempo does **not** come back down
 * on its own; that would be a punishment for having a life.
 */
export function readAgo(at: number | null, now: number): string | null {
  if (at === null || at <= 0 || now < at) {
    return null;
  }

  const days = Math.floor((now - at) / 86_400_000);

  if (days === 0) {
    return 'lu aujourd’hui';
  }

  if (days === 1) {
    return 'lu hier';
  }

  return `lu il y a ${days} jours`;
}

/** Where the traversal stopped, as a fraction of the piece, for the rail. */
export function stopPositions(
  interruptions: readonly Pick<Interruption, 'startTick'>[],
  startTick: number,
  endTick: number,
): readonly number[] {
  const span = endTick - startTick;

  if (span <= 0) {
    return [];
  }

  return interruptions.map((stop) =>
    Math.min(1, Math.max(0, (stop.startTick - startTick) / span)),
  );
}

// ── What to take up again ─────────────────────────────────────────

/** One line of the work list. */
export type ReworkTarget = {
  /** Index into the journey's units. */
  readonly index: number;
  /** The cell it belongs to, for the name. */
  readonly section: PieceSection;
  /** The unit's own tempo, when it differs from the piece's. */
  readonly rate: number;
  /** Why it is on the list. Ordering only — never shown. */
  readonly reason: 'stopped' | 'holes' | 'slow';
};

/**
 * The two to four passages worth taking up again, in order.
 *
 * This is the act phase 2 repeats, and the traversal exists to feed it: four
 * passes on one unit cost **54 s in the median** and buy the five to twenty
 * repetitions consolidation asks for, where a traversal buys one, for six
 * minutes.
 *
 * Order of certainty, not of severity: where the player came off the rails
 * (a fact about this traversal), then where attacks went unanswered (a
 * measure on this traversal), then what is slower than the piece (a standing
 * fact, true with no traversal at all — which is what makes the list useful
 * on the very first screen).
 *
 * It replaced a list of **one line per unit**: 38 in the median, 96 at the
 * p90, 483 at the maximum, and more than ten in **91.5% of pieces**.
 */
export function toRework(
  phrases: readonly Phrase[],
  cells: readonly PieceSection[],
  groups: readonly PracticeGroup[],
  reading: RunReading | null,
  rateOf: (index: number) => number | null,
  pieceTempo: number,
  bpm: number,
  ppq: number,
  limit: number,
): readonly ReworkTarget[] {
  const cellOf = new Map<number, PieceSection>();

  for (const cell of cells) {
    for (const index of cell.unitIndices) {
      cellOf.set(index, cell);
    }
  }

  const scored = phrases.map((phrase, index) => {
    const rate =
      rateOf(index) ?? startingRate(rhythmGroups(phrase), bpm, ppq);
    const inside = { total: 0, answered: 0, stopped: false };

    if (reading !== null) {
      for (let g = 0; g < groups.length; g += 1) {
        const tick = groups[g].startTick;

        if (tick < phrase.startTick || tick >= phrase.endTick) {
          continue;
        }

        inside.total += 1;

        if (reading.honoured[g] === true) {
          inside.answered += 1;
        }
      }

      inside.stopped = reading.interruptions.some(
        (stop) =>
          stop.startTick >= phrase.startTick && stop.startTick < phrase.endTick,
      );
    }

    const honoured = inside.total === 0 ? 1 : inside.answered / inside.total;

    return { index, phrase, rate, honoured, stopped: inside.stopped };
  });

  const picked = scored
    .filter(
      (s) =>
        s.stopped || s.honoured < PLAYED_THROUGH_RATIO || s.rate < pieceTempo,
    )
    .sort((a, b) => {
      if (a.stopped !== b.stopped) {
        return a.stopped ? -1 : 1;
      }

      if (a.honoured !== b.honoured) {
        return a.honoured - b.honoured;
      }

      if (a.rate !== b.rate) {
        return a.rate - b.rate;
      }

      return a.index - b.index;
    })
    .slice(0, Math.max(0, limit));

  return picked.map((s) => ({
    index: s.index,
    section: cellOf.get(s.index) ?? {
      id: 'unknown',
      label: null,
      startTick: s.phrase.startTick,
      endTick: s.phrase.endTick,
      startMeasure: s.phrase.startMeasure,
      endMeasure: s.phrase.endMeasure,
      unitIndices: [s.index],
    },
    rate: s.rate,
    reason: s.stopped ? 'stopped' : s.honoured < PLAYED_THROUGH_RATIO ? 'holes' : 'slow',
  }));
}

/** How a line of the work list names its place. */
export function reworkLabel(target: ReworkTarget, phrase: Phrase): string {
  const bars =
    phrase.startMeasure === phrase.endMeasure
      ? `mesure ${phrase.startMeasure}`
      : `mesures ${phrase.startMeasure}–${phrase.endMeasure}`;

  if (target.section.label !== null && target.section.label !== '') {
    return `${target.section.label} · ${bars}`;
  }

  return bars.charAt(0).toUpperCase() + bars.slice(1);
}

// ── The verdict of a traversal ────────────────────────────────────

/**
 * What the screen says about the traversal that just happened.
 *
 * Three outcomes and no fourth. « Tu es allé au bout » alone would be a lie
 * on a pass where a third of the notes never sounded — and it is exactly the
 * lie the phase told, since its criterion never consulted a pitch. The
 * length lottery is why the rule cannot simply be phase 1's: at 95% of
 * attacks answered, the same player stops **0.8% of the time on a unit and
 * 21.0% on a traversal**. "Don't stop" is the right rule for twelve attacks
 * and the wrong one for five hundred and thirty-one.
 */
export function traversalLine(
  reading: RunReading | null,
  honoured: number,
  effectiveBpm: number,
  firstStopBar: number | null,
): string | null {
  if (reading === null) {
    return null;
  }

  if (reading.interruptions.length > 0) {
    return firstStopBar === null
      ? 'Tu t’es arrêté en route.'
      : `Tu t’es arrêté à la mesure ${firstStopBar}.`;
  }

  if (honoured >= PLAYED_THROUGH_RATIO) {
    return `Tu as tenu le morceau en entier, à ${effectiveBpm}.`;
  }

  return 'Tu es allé au bout. Des passages t’ont échappé.';
}

/** True when the traversal earns the next rung of the ladder. */
export function heldThrough(reading: RunReading | null, honoured: number): boolean {
  return (
    reading !== null &&
    reading.interruptions.length === 0 &&
    honoured >= PLAYED_THROUGH_RATIO
  );
}
